import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { useMediaQuery, glyphs, fonts, colors } from "../../Styles";
import { NewInput, NewSelect, Setting, NewButtonList } from "..";
import { ListStyle_Types, NewFieldStyles, NewInputHandles } from "../../Definitions";
import { Graph_QL, useSystem } from "../../Services";
import { RootState, useAppSelector } from "../../Store";
import { useDispatch, useSelector } from "react-redux";
import { SET_EDIT_MODE } from "../../Store/UI/editModeSlice";
import { useSystemInfoById } from "../../Services/Agrisys_GqlDataCaller/SystemCalls/systemSlice";

const SYSTEM_ID = 1;

export default function SystemSettings() {
  const { t } = useTranslation();
  const mobileXs = useMediaQuery(`(max-width: 570px)`);
  const responsiveBox = useMediaQuery(`(max-width: 1420px)`);
  
  // #region | GraphQL
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);

  const { systemId: urlSystemId } = useParams(); // TODO routing changes in order to get a correct system info
  const systemId = urlSystemId ? +urlSystemId : SYSTEM_ID;
  const { maintainEntries: maintainSystem } = useSystem(connection);
  
  const {isLoading, entity: system} = useSystemInfoById(systemId);

  // #endregion

  // #region | Feed Speed
  const minFeedSpeedRef = useRef<NewInputHandles | null>(null);
  const maxFeedSpeedRef = useRef<NewInputHandles | null>(null);

  const [minFeedSpeed, setMinFeedSpeed] = useState(system?.min_feed_speed);
  const [maxFeedSpeed, setMaxFeedSpeed] = useState(system?.max_feed_speed);

  useEffect(() => {
    setMinFeedSpeed(system?.min_feed_speed);
    setMaxFeedSpeed(system?.max_feed_speed);
  },[system?.max_feed_speed, system?.min_feed_speed])
  // #endregion

  // #region | System Name
  const systemNameRef = useRef<NewInputHandles | null>(null);

  const [systemName, setSystemName] = useState(system?.name);

  useEffect(() => {
    setSystemName(system?.name);
  },[system?.name])
  // #endregion

  // #region | Feed Amount
  const feedAmountRef = useRef<NewInputHandles | null>(null);

  const [feedAmount, setFeedAmount] = useState(system?.default_amount);

  useEffect(() => {
    setFeedAmount(system?.default_amount);
  }, [system?.default_amount])
  // #endregion
  
  // #region | Daily Rotation
  const dailyRotationOptions = [
    { label: t("off", { defaultValue: "off" }), id: 0 },
    { label: t("on", { defaultValue: "on" }), id: 1 },
  ];

  const programmedInfo = useRef<string | null>(null);
  const [dailyRotation, setDailyRotation] = useState<number>(parseInt(system?.programmed.toString(2).at(-2) ?? '0'));
  
  useEffect(() => {
    const bitListString = system?.programmed.toString(2).padStart(8,'0');
    programmedInfo.current = bitListString ?? null;
    setDailyRotation(parseInt(bitListString?.at(-2) ?? '0'));
  },[system?.programmed])

  useEffect(() => {
    const bitList = programmedInfo.current?.split('');
    if (bitList) {
      bitList[8-2] = dailyRotation.toString(2);
      programmedInfo.current = bitList.join("")
    }
  },[dailyRotation])
  // #endregion

  // #region | Feed Balance
  // const feedBalanceRef = useRef<NewInputHandles | null>(null);
  // const [unit, setUnit] = useState<number>(system?.feed_balance_unit ?? 0);

  // useEffect(() => {
  //   if (system?.feed_balance_unit) {
  //     setUnit(system.feed_balance_unit)
  //   }
  // },[system?.feed_balance_unit])

  // const unitOptions = [
  //   { label: t("units.kg", { defaultValue: "kg" }), id: 1 },
  //   { label: t("units.pct", { defaultValue: "pct" }), id: 2 },
  // ];
  // #endregion

  // #region | Feed Skip
  // const timePickerValueRef = useRef<{ hour: string | undefined, minute: string | undefined }>({ hour: undefined, minute: undefined });
  // const [defaultFeedSkpi, setDefaultFeedSkpi] = useState({defaultHour: convertMinutesFromMidnightToHour(farmSettings?.feed_skip_lf ?? 0), defaultMinute: convertMinutesFromMidnightToMinute(farmSettings?.feed_skip_lf ?? 0)})
  // #endregion

  // #region | Feed in Silos
  // #endregion
  
  // #region |  Dirty Data
  const dirtyData = useMemo(() => {
    if (!system) return false;
  
    return (
      minFeedSpeed !== system.min_feed_speed ||
      maxFeedSpeed !== system.max_feed_speed ||
      dailyRotation !== parseInt(system.programmed.toString(2).at(-2) ?? '0') ||
      feedAmount !== system.default_amount ||
      systemName !== system.name
    );
  }, [
    system,
    dailyRotation,
    feedAmount,
    maxFeedSpeed,
    minFeedSpeed,
    systemName
  ]);

  const dispatchEditMode = useDispatch();
  const editMode = useSelector((state: RootState) => state.uiState.editMode.editMode);
  const editType = useSelector((state: RootState) => state.uiState.editMode.editType);

  useEffect(() => {
    if (!system) return;
    dispatchEditMode(SET_EDIT_MODE({ enabled: false, type: undefined }))
    if (dirtyData) {
      dispatchEditMode(SET_EDIT_MODE({ enabled: true, type: "system-settings" }));
    }
  }, [dirtyData, dispatchEditMode, system]);
  // #endregion

  return (<>
    <div style={{ zIndex: editMode && editType === "system-settings" ? 9999 : 0, position: "relative", display: "flex", gap: "20px", alignItems: mobileXs ? "center" : "end", justifyContent: "center", flexDirection: "column", width: !responsiveBox ? "1050px" : "100%" }}>
      
      {/* Name */}
      <Setting header={t("locationFeederPages.settings.systemName", { defaultValue: "System Name" })}
        startEnhancer={glyphs.Setting({ size: "25px" })}
        content={
          <NewInput
            ref={systemNameRef}
            onChange={(e) => setSystemName(e)}
            fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
            valueOverrides={{ defaultValue: systemName }}
          />
        }
      />

      {/* Daily Rotation */}
      <Setting header={t("locationFeederPages.settings.dailyRotation", { defaultValue: "Daily Rotation" })}
        startEnhancer={glyphs.Setting({ size: "25px" })}
        content={ <div style={{ display: "flex", flexDirection: "column", alignItems: mobileXs ? "center" : "start" }} >
          <NewSelect
            options={dailyRotationOptions}
            onChange={(e) => setDailyRotation(Number(e?.id ?? 0))}
            fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
            valueOverrides={{ defaultValue: dailyRotationOptions[dailyRotation] }}
          />
          <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, marginTop: "5px", textAlign: mobileXs ? "center" : "left" }} >
            {t("locationFeederPages.settings.dailyRotationDisclaimer", { defaultValue: "NB: If the location is empty or the animal has no feed curve assigned, the motor will perform a daily rotation for service purposes." })}
          </p>
        </div> }
      />

      {/* Feed in Silos */}
      {/* <Setting
        header={t("locationFeederPages.settings.feedInSilos", { defaultValue: "Feed in Silos" })}
        startEnhancer={glyphs.Setting({ size: "25px" })}
        content={<></>}
      /> */}

      {/* Feed Speed */}
      <Setting
        header={t("feed.feedCurve.feedSpeed", { defaultValue: "feed speed" })}
        startEnhancer={glyphs.Setting({ size: "25px" })}
        content={ <div style={{ display: "flex", flexWrap: "wrap", rowGap: "10px", columnGap: "5px", width: "100%", justifyContent: mobileXs ? "center" : "start" }} >
          <NewInput
            header={t("feed.feedCurve.minFeedSpeed", { defaultValue: "minimum feed speed (g/min)" })}
            ref={minFeedSpeedRef}
            onChange={(e) => setMinFeedSpeed(Number(e))}
            fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
            valueOverrides={{ defaultValue: minFeedSpeed, valueType: "number", minValueNumber: 0 }}
            placeholder={`${t("units.g", { defaultValue: "g" })}/${t("units.min", { defaultValue: "min" })}`}
          />
          <NewInput
            header={t("feed.feedCurve.maxFeedSpeed", { defaultValue: "maximum feed speed (g/min)" })}
            ref={maxFeedSpeedRef}
            onChange={(e) => setMaxFeedSpeed(Number(e))}
            fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
            valueOverrides={{ defaultValue: maxFeedSpeed, valueType: "number", minValueNumber: 0 }}
            placeholder={`${t("units.g", { defaultValue: "g" })}/${t( "units.min", { defaultValue: "min" } )}`}
          />
        </div> }
      />

      {/* Feed Amount */}
      <Setting
        header={t("locationFeederPages.settings.feedAmount", { defaultValue: "Feed Amount" })}
        startEnhancer={glyphs.Setting({ size: "25px" })}
        content={ <div style={{ display: "flex", flexWrap: "wrap", rowGap: "10px", columnGap: "5px", width: "100%", justifyContent: mobileXs ? "center" : "start" }} >
          <NewInput
            header={t("locationFeederPages.settings.defFeedAmount", { defaultValue: "default feed amount (g)" })}
            ref={feedAmountRef}
            onChange={(e) => setFeedAmount(Number(e))}
            fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
            valueOverrides={{ defaultValue: feedAmount, valueType: "number", minValueNumber: 0 }}
            placeholder={`${t("units.g", { defaultValue: "g" })}`}
          />
        </div> }
      />

      {/* Feed Balance */}
      {/* <Setting
        header={t("feed.general.feedBalance", { defaultValue: "Feed Balance" })}
        startEnhancer={glyphs.Condition({ size: "25px" })}
        content={ <div style={{ display: "flex", flexWrap: "wrap", rowGap: "10px", columnGap: "5px", width: "100%", justifyContent: mobileXs ? "center" : "start" }} >
          <NewSelect
            header={t("locationFeederPages.settings.units", { defaultValue: "Units" })}
            options={unitOptions}
            onChange={(e) => setUnit(Number(e?.id ?? 0))}
            fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
            valueOverrides={{ defaultValue: unitOptions.find((option) => option.id === unit) ?? unitOptions[0] }}
          />
          <NewInput
            header={t("locationFeederPages.settings.attentionLimit", { defaultValue: "Attention Limit" }) + (unit === 0 ? ` (${t("units.kg", { defaultValue: "KG" })})` : ` (${t("units.pct", { defaultValue: "PCT" })})`) }
            ref={feedBalanceRef}
            fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
            placeholder={unit === 0 ? t("units.kg", { defaultValue: "KG" }) : t("units.pct", { defaultValue: "PCT" })}
            valueOverrides={{valueType: "number", defaultValue: system?.feed_balance ? Math.floor(system.feed_balance/10)/100 : undefined}}
          />
        </div> }
      /> */}

      {/* Standard Feed Skip */}
      {/* <Setting
        header={t("feed.general.feedSkip", { defaultValue: "Feed Skip" })}
        startEnhancer={glyphs.Time({ size: "25px" })}
        content={ <div style={{ display: "flex", flexWrap: "wrap", rowGap: "10px", columnGap: "5px", width: "100%", justifyContent: mobileXs ? "center" : "start" }} >
          <NewTimePicker
            header={t("locationFeederPages.settings.FeedSkip", { defaultValue: "Standard Feed Skip Duration" }) + ` (${t("units.hours", { defaultValue: "Hours" })}:${t("units.minutes", { defaultValue: "Minutes" })})`}
            valueRef={timePickerValueRef}
            overrides={{
              fieldStyle: NewFieldStyles.greyLightSecondary
            }}
            background
            // valueOverrides={defaultFeedDayStart}
          />
        </div> }
      /> */}
      </div>
      {/* Save/discard btns */}
      {dirtyData && createPortal(
        <div style={{ position: "absolute", zIndex: editMode && editType === "system-settings" ? 9999 : 0, bottom: "40px", left: "50%", transform: "translateX(-50%)", backgroundColor: colors.greyDarkPrimary, padding: "20px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px" + colors.blackPrimary + "4d", }}>
        <NewButtonList 
          contents={[t("globalSettingsPages.globalSettings.saveChanges", {defaultValue: "save changes"}), t("globalSettingsPages.globalSettings.discardChanges", {defaultValue: "discard changes"})]} 
          onClick={(i => {
            if (i === 0) {
              if (maintainSystem && system) {
                if ( !dirtyData ) {
                  return;
                }
            
                const newProgrammed = parseInt(programmedInfo.current ?? '0',2)
                const min_feed_speed = minFeedSpeed=== system?.min_feed_speed ? undefined : minFeedSpeed;
                const max_feed_speed = maxFeedSpeed === system?.max_feed_speed ? undefined : maxFeedSpeed;
                const programmed = newProgrammed === system?.programmed ? undefined : newProgrammed;
                const default_amount = feedAmount === system?.default_amount ? undefined : feedAmount;
                const system_name = systemName === system?.name ? undefined : systemName;
              
                maintainSystem({data: {system_id: system.system_id, min_feed_speed, max_feed_speed, programmed, default_amount, name: system_name}})
                dispatchEditMode(SET_EDIT_MODE({ enabled: false, type: undefined }));
              }
            }
            else if (i === 1) {
              dispatchEditMode(SET_EDIT_MODE({ enabled: false, type: undefined }));
              setMaxFeedSpeed(system?.max_feed_speed);
              setMinFeedSpeed(system?.min_feed_speed);
              setDailyRotation(parseInt(system?.programmed.toString(2).at(-2) ?? '0'));
              setFeedAmount(system?.default_amount)
              setSystemName(system?.name);
            }
          })}
          overrides={(i) => ({
            baseStyles: { backgroundColor: i === 0 ? colors.blueDarkPrimary : colors.greyDarkPrimary, border: i === 1 ? "3px solid "+colors.blueDarkPrimary : "none"},
            hoverStyles: { backgroundColor: i === 0 ? colors.blueDarkSecondary : colors.blueDarkPrimary },
          })}
          listOverrides={{
            listStyle: mobileXs ? ListStyle_Types.vertical : ListStyle_Types.horizontal
          }} 
        />
      </div>, document.body)
    }
  </>);
}
